<div class="p-6 container mx-auto space-y-6">
  <!-- Header -->
  <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-base-300 pb-4">
    <div>
      <h1 class="text-2xl font-bold text-base-content">Academy Settings & Configuration</h1>
      <p class="text-sm opacity-70 mt-1">
        Customize your academy's branding, legal entity & invoicing information, and direct bank deposit details.
      </p>
    </div>
    <span class="daisy-badge daisy-badge-primary text-xs font-semibold px-3 py-2">
      {{ currentTenant?.subdomain || 'default' }}.learnbird.lk
    </span>
  </div>

  @if (loading) {
    <div class="flex justify-center py-12">
      <span class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"></span>
    </div>
  } @else {
    <!-- Tabs Header -->
    <div class="daisy-tabs daisy-tabs-boxed bg-base-200 p-1.5 rounded-xl grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-1">
      <button
        type="button"
        (click)="setActiveTab('branding')"
        class="daisy-tab daisy-tab-lg font-medium transition-all gap-2 flex items-center justify-center rounded-lg"
        [class.daisy-tab-active]="activeTab === 'branding'"
      >
        <i class="fa-solid fa-palette text-sm"></i>
        <span>Branding & Theme</span>
      </button>
      <button
        type="button"
        (click)="setActiveTab('landingPage')"
        class="daisy-tab daisy-tab-lg font-medium transition-all gap-2 flex items-center justify-center rounded-lg"
        [class.daisy-tab-active]="activeTab === 'landingPage'"
      >
        <i class="fa-solid fa-globe text-sm"></i>
        <span>Website & Landing</span>
      </button>
      <button
        type="button"
        (click)="setActiveTab('policies')"
        class="daisy-tab daisy-tab-lg font-medium transition-all gap-2 flex items-center justify-center rounded-lg"
        [class.daisy-tab-active]="activeTab === 'policies'"
      >
        <i class="fa-solid fa-file-contract text-sm"></i>
        <span>Terms & Privacy</span>
      </button>
      <button
        type="button"
        (click)="setActiveTab('legal')"
        class="daisy-tab daisy-tab-lg font-medium transition-all gap-2 flex items-center justify-center rounded-lg"
        [class.daisy-tab-active]="activeTab === 'legal'"
      >
        <i class="fa-solid fa-scale-balanced text-sm"></i>
        <span>Legal & Invoicing</span>
      </button>
      <button
        type="button"
        (click)="setActiveTab('payments')"
        class="daisy-tab daisy-tab-lg font-medium transition-all gap-2 flex items-center justify-center rounded-lg"
        [class.daisy-tab-active]="activeTab === 'payments'"
      >
        <i class="fa-solid fa-building-columns text-sm"></i>
        <span>Payments & Bank Transfer</span>
      </button>
    </div>

    <form [formGroup]="settingsForm" (ngSubmit)="onSubmit()" class="space-y-6">
      <!-- ========================================== -->
      <!-- TAB 1: BRANDING & THEME                    -->
      <!-- ========================================== -->
      @if (activeTab === 'branding') {
        <div class="space-y-6">
          <!-- General Settings -->
          <div class="daisy-card bg-base-100 shadow-md p-6 border border-base-200">
            <h2 class="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
              <i class="fa-solid fa-sliders text-primary"></i> General Academy Info
            </h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="daisy-form-control w-full">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium">Academy Name</span>
                </label>
                <input
                  type="text"
                  formControlName="name"
                  placeholder="My Academy"
                  class="daisy-input daisy-input-bordered w-full"
                />
              </div>

              <div class="daisy-form-control w-full">
                <label class="daisy-label">
                  <span class="daisy-label-text font-medium">Subdomain</span>
                </label>
                <div class="flex">
                  <input
                    type="text"
                    formControlName="subdomain"
                    class="daisy-input daisy-input-bordered w-full rounded-r-none bg-base-200"
                  />
                  <span class="inline-flex items-center px-3 rounded-r-lg border border-l-0 border-base-300 bg-base-200 text-base-content/70 text-sm font-mono">
                    .learnbird.lk
                  </span>
                </div>
              </div>
            </div>

            <div class="mt-4 pt-4 border-t border-base-200">
              <label class="daisy-label cursor-pointer justify-start gap-3 py-1">
                <input
                  type="checkbox"
                  formControlName="autoPublishLiveClasses"
                  class="daisy-toggle daisy-toggle-primary"
                />
                <div>
                  <span class="daisy-label-text font-medium">Auto-Publish Live Classes</span>
                  <p class="text-xs text-base-content/60">Automatically make newly created live classes publicly visible to students</p>
                </div>
              </label>
            </div>
          </div>

          <!-- Theme & Branding Selection -->
          <div class="daisy-card bg-base-100 shadow-md p-6 border border-base-200">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <h2 class="text-lg font-semibold text-base-content flex items-center gap-2">
                  <i class="fa-solid fa-palette text-primary"></i> Theme & Color Palette
                </h2>
                <p class="text-xs text-base-content opacity-70 mt-0.5">
                  Choose from prebuilt themes or specify custom brand colors for your academy.
                </p>
              </div>

              <!-- Mode Switcher -->
              <div class="daisy-join self-start sm:self-auto">
                <button
                  type="button"
                  (click)="setThemeMode('prebuilt')"
                  class="daisy-btn daisy-btn-sm daisy-join-item"
                  [class.daisy-btn-primary]="themeMode === 'prebuilt'"
                  [class.daisy-btn-outline]="themeMode !== 'prebuilt'"
                >
                  <i class="fa-solid fa-table-cells"></i> Prebuilt Themes
                </button>
                <button
                  type="button"
                  (click)="setThemeMode('custom')"
                  class="daisy-btn daisy-btn-sm daisy-join-item"
                  [class.daisy-btn-primary]="themeMode === 'custom'"
                  [class.daisy-btn-outline]="themeMode !== 'custom'"
                >
                  <i class="fa-solid fa-sliders"></i> Custom Colors
                </button>
              </div>
            </div>

            @if (themeMode === 'prebuilt') {
              <!-- Prebuilt Theme Grid -->
              <div class="space-y-3">
                <label class="daisy-label pb-0">
                  <span class="daisy-label-text font-medium">Select Prebuilt Theme ({{ themeOptions.length }} Available)</span>
                </label>
                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 max-h-72 overflow-y-auto p-1 border border-base-200 rounded-lg bg-base-200/50">
                  @for (t of themeOptions; track t.id) {
                    <button
                      type="button"
                      (click)="selectPrebuiltTheme(t.id)"
                      class="flex flex-col p-2.5 rounded-lg border text-left transition-all cursor-pointer hover:scale-102"
                      [class.border-primary]="settingsForm.value.theme === t.id"
                      [class.ring-2]="settingsForm.value.theme === t.id"
                      [class.ring-primary]="settingsForm.value.theme === t.id"
                      [class.border-base-300]="settingsForm.value.theme !== t.id"
                      [class.bg-base-100]="settingsForm.value.theme !== t.id"
                      [class.bg-primary/10]="settingsForm.value.theme === t.id"
                    >
                      <div class="flex items-center justify-between gap-1 w-full mb-2">
                        <span class="text-xs font-semibold truncate text-base-content">{{ t.name }}</span>
                        @if (t.isDark) {
                          <i class="fa-solid fa-moon text-[10px] opacity-60"></i>
                        } @else {
                          <i class="fa-solid fa-sun text-[10px] opacity-60"></i>
                        }
                      </div>
                      <!-- Color Swatches -->
                      <div class="flex items-center gap-1 w-full h-4 rounded overflow-hidden shadow-inner border border-base-300">
                        <div class="h-full flex-1" [style.background-color]="t.primary"></div>
                        <div class="h-full flex-1" [style.background-color]="t.secondary"></div>
                        <div class="h-full flex-1" [style.background-color]="t.accent"></div>
                        <div class="h-full flex-1" [style.background-color]="t.base100"></div>
                      </div>
                    </button>
                  }
                </div>
              </div>
            } @else {
              <!-- Custom Colors Pickers -->
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 border border-base-200 rounded-lg p-4 bg-base-200/40">
                <!-- Primary Color & Color on Primary -->
                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium">Primary Color</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="primaryColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="primaryColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium text-xs">Color on Primary (Text)</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="primaryContentColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="primaryContentColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <!-- Secondary Color & Color on Secondary -->
                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium">Secondary Color</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="secondaryColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="secondaryColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium text-xs">Color on Secondary (Text)</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="secondaryContentColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="secondaryContentColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <!-- Accent Color & Color on Accent -->
                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium">Accent Highlight</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="accentColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="accentColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium text-xs">Color on Accent (Text)</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="accentContentColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="accentContentColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <!-- Neutral Color & Color on Neutral -->
                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium">Neutral / Dark Color</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="neutralColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="neutralColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium text-xs">Color on Neutral (Text)</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="neutralContentColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="neutralContentColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <!-- Base Canvas Background & Canvas Text Color -->
                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium">Base Canvas Background</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="baseColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="baseColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>

                <div class="daisy-form-control w-full">
                  <label class="daisy-label">
                    <span class="daisy-label-text font-medium text-xs">Canvas Text Color</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      formControlName="baseContentColor"
                      (change)="onBrandingChange()"
                      class="w-10 h-9 rounded cursor-pointer border border-base-300 bg-transparent"
                    />
                    <input
                      type="text"
                      formControlName="baseContentColor"
                      (keyup)="onBrandingChange()"
                      class="daisy-input daisy-input-bordered daisy-input-sm flex-1 uppercase font-mono text-xs"
                    />
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Logo & Favicon Upload -->
          <div class="daisy-card bg-base-100 shadow-md p-6 border border-base-200 space-y-6">
            <div>
              <h2 class="text-lg font-semibold text-base-content flex items-center gap-2">
                <i class="fa-solid fa-image text-primary"></i> Academy Brand Assets (Logo & Favicon)
              </h2>
              <p class="text-xs text-base-content opacity-70 mt-0.5">
                Upload custom logo and favicon images or specify external URLs. Uploaded images are hosted on high-speed CDN storage.
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Logo Asset Box -->
              <div class="border border-base-200 rounded-xl p-4 bg-base-200/40 flex flex-col justify-between space-y-4">
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <span class="font-medium text-sm text-base-content flex items-center gap-2">
                      <i class="fa-solid fa-icons text-primary"></i> Academy Logo
                    </span>
                    @if (settingsForm.value.logo) {
                      <span class="daisy-badge daisy-badge-success daisy-badge-sm gap-1 text-[11px]">
                        <i class="fa-solid fa-check"></i> Active
                      </span>
                    }
                  </div>

                  <!-- Preview -->
                  <div class="h-28 rounded-lg border border-base-300 bg-base-100 flex items-center justify-center p-3 overflow-hidden relative group">
                    @if (settingsForm.value.logo) {
                      <img
                        [src]="settingsForm.value.logo"
                        alt="Academy Logo Preview"
                        class="max-h-full max-w-full object-contain"
                      />
                    } @else {
                      <div class="text-center text-base-content/40">
                        <i class="fa-solid fa-building text-3xl mb-1 block"></i>
                        <span class="text-xs">No Logo Uploaded</span>
                      </div>
                    }
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="flex items-center gap-2">
                    <label class="daisy-btn daisy-btn-primary daisy-btn-sm flex-1 text-xs gap-2 cursor-pointer">
                      @if (uploadingLogo) {
                        <span class="daisy-loading daisy-loading-spinner daisy-loading-xs"></span>
                        Uploading Logo...
                      } @else {
                        <i class="fa-solid fa-cloud-arrow-up"></i> Upload Logo Image
                      }
                      <input
                        type="file"
                        accept="image/*"
                        class="hidden"
                        (change)="onAssetUpload($event, 'logo')"
                        [disabled]="uploadingLogo"
                      />
                    </label>

                    @if (settingsForm.value.logo) {
                      <button
                        type="button"
                        (click)="clearAsset('logo')"
                        class="daisy-btn daisy-btn-ghost daisy-btn-sm text-xs text-error gap-1"
                        title="Remove Logo"
                      >
                        <i class="fa-solid fa-trash"></i>
                      </button>
                    }
                  </div>

                  <div class="daisy-form-control">
                    <input
                      type="text"
                      formControlName="logo"
                      (keyup)="onBrandingChange()"
                      placeholder="Or paste image URL (e.g. https://domain.com/logo.png)"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs w-full"
                    />
                  </div>
                </div>
              </div>

              <!-- Favicon Asset Box -->
              <div class="border border-base-200 rounded-xl p-4 bg-base-200/40 flex flex-col justify-between space-y-4">
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <span class="font-medium text-sm text-base-content flex items-center gap-2">
                      <i class="fa-solid fa-globe text-secondary"></i> Academy Favicon
                    </span>
                    @if (settingsForm.value.favicon) {
                      <span class="daisy-badge daisy-badge-success daisy-badge-sm gap-1 text-[11px]">
                        <i class="fa-solid fa-check"></i> Active
                      </span>
                    }
                  </div>

                  <!-- Preview -->
                  <div class="h-28 rounded-lg border border-base-300 bg-base-100 flex items-center justify-center p-3 relative">
                    <div class="flex items-center gap-3 bg-base-200 px-3 py-1.5 rounded-md border border-base-300 max-w-full">
                      <div class="w-6 h-6 rounded bg-base-100 border border-base-300 flex items-center justify-center overflow-hidden shrink-0">
                        @if (settingsForm.value.favicon) {
                          <img [src]="settingsForm.value.favicon" alt="Favicon" class="w-full h-full object-contain" />
                        } @else {
                          <i class="fa-solid fa-globe text-xs text-base-content/40"></i>
                        }
                      </div>
                      <span class="text-xs text-base-content/70 truncate font-mono">Browser Tab Icon Preview</span>
                    </div>
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="flex items-center gap-2">
                    <label class="daisy-btn daisy-btn-secondary daisy-btn-sm flex-1 text-xs gap-2 cursor-pointer">
                      @if (uploadingFavicon) {
                        <span class="daisy-loading daisy-loading-spinner daisy-loading-xs"></span>
                        Uploading Favicon...
                      } @else {
                        <i class="fa-solid fa-cloud-arrow-up"></i> Upload Favicon Icon
                      }
                      <input
                        type="file"
                        accept="image/x-icon,image/png,image/svg+xml,image/jpeg"
                        class="hidden"
                        (change)="onAssetUpload($event, 'favicon')"
                        [disabled]="uploadingFavicon"
                      />
                    </label>

                    @if (settingsForm.value.favicon) {
                      <button
                        type="button"
                        (click)="clearAsset('favicon')"
                        class="daisy-btn daisy-btn-ghost daisy-btn-sm text-xs text-error gap-1"
                        title="Remove Favicon"
                      >
                        <i class="fa-solid fa-trash"></i>
                      </button>
                    }
                  </div>

                  <div class="daisy-form-control">
                    <input
                      type="text"
                      formControlName="favicon"
                      (keyup)="onBrandingChange()"
                      placeholder="Or paste favicon URL (e.g. https://domain.com/favicon.ico)"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- ========================================== -->
      <!-- TAB: TERMS & PRIVACY POLICIES              -->
      <!-- ========================================== -->
      @if (activeTab === 'policies') {
        <div class="space-y-6">
          <div class="daisy-card bg-base-100 shadow-md p-6 border border-base-200">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-base-200 pb-4">
              <div>
                <h2 class="text-lg font-semibold text-base-content flex items-center gap-2">
                  <i class="fa-solid fa-file-contract text-primary"></i> Academy Terms & Privacy Policies
                </h2>
                <p class="text-xs text-base-content opacity-70 mt-0.5">
                  Provide custom legal agreements for your academy. You can use platform defaults, link to your external documents, or draft tailored terms using the rich text editor.
                </p>
              </div>
            </div>

            <!-- SECTION 1: TERMS AND CONDITIONS -->
            <div class="mt-6 space-y-4">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-base-200 pb-2">
                <div>
                  <h3 class="font-bold text-sm text-base-content flex items-center gap-2">
                    <i class="fa-solid fa-scale-balanced text-primary"></i> Terms and Conditions
                  </h3>
                  <p class="text-xs text-base-content/70">
                    Governs student registrations, subscriptions, intellectual property, and acceptable conduct.
                  </p>
                </div>
                <a
                  routerLink="/terms"
                  target="_blank"
                  class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary gap-1.5 self-start sm:self-auto"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i> View Live Page
                </a>
              </div>

              <!-- Mode Selection -->
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.termsMode === 'default'"
                  [class.bg-primary/5]="settingsForm.value.termsMode === 'default'"
                  [class.border-base-300]="settingsForm.value.termsMode !== 'default'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="termsMode"
                      value="default"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">Platform Default</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Standard platform terms with your academy name dynamically applied.
                      </span>
                    </div>
                  </div>
                </label>

                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.termsMode === 'link'"
                  [class.bg-primary/5]="settingsForm.value.termsMode === 'link'"
                  [class.border-base-300]="settingsForm.value.termsMode !== 'link'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="termsMode"
                      value="link"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">External Link</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Redirect visitors directly to your hosted terms URL.
                      </span>
                    </div>
                  </div>
                </label>

                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.termsMode === 'custom'"
                  [class.bg-primary/5]="settingsForm.value.termsMode === 'custom'"
                  [class.border-base-300]="settingsForm.value.termsMode !== 'custom'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="termsMode"
                      value="custom"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">Custom Page</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Write and design custom terms using the rich text editor.
                      </span>
                    </div>
                  </div>
                </label>
              </div>

              <!-- External Link Configuration -->
              @if (settingsForm.value.termsMode === 'link') {
                <div class="daisy-card bg-base-200/40 border border-base-300 p-4 space-y-2">
                  <label class="daisy-label py-0">
                    <span class="daisy-label-text font-medium text-xs">External Terms URL</span>
                  </label>
                  <div class="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      formControlName="termsUrl"
                      placeholder="https://myacademy.com/terms-and-conditions"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs flex-1"
                    />
                    @if (settingsForm.value.termsUrl) {
                      <a
                        [href]="settingsForm.value.termsUrl"
                        target="_blank"
                        class="daisy-btn daisy-btn-sm daisy-btn-outline gap-1.5"
                      >
                        <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i> Test Link
                      </a>
                    }
                  </div>
                  <p class="text-[11px] text-base-content/60">
                    Visitors navigating to <code>/terms</code> on your academy site will be automatically redirected to this URL.
                  </p>
                </div>
              }

              <!-- Custom Quill Editor Configuration -->
              @if (settingsForm.value.termsMode === 'custom') {
                <div class="space-y-3 pt-2">
                  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label class="daisy-label py-0">
                      <span class="daisy-label-text font-medium text-xs">Terms & Conditions Content</span>
                    </label>
                    <button
                      type="button"
                      (click)="loadTermsTemplate()"
                      class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-secondary gap-1.5"
                    >
                      <i class="fa-solid fa-file-lines text-[10px]"></i> Load Boilerplate Template
                    </button>
                  </div>
                  <black-rich-text-editor
                    formControlName="termsContent"
                    placeholder="Draft your Terms and Conditions..."
                    theme="snow"
                    minHeight="300px"
                  ></black-rich-text-editor>
                </div>
              }
            </div>

            <!-- SECTION 2: PRIVACY POLICY -->
            <div class="mt-10 space-y-4 pt-6 border-t border-base-200">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-base-200 pb-2">
                <div>
                  <h3 class="font-bold text-sm text-base-content flex items-center gap-2">
                    <i class="fa-solid fa-shield-halved text-primary"></i> Privacy Policy
                  </h3>
                  <p class="text-xs text-base-content/70">
                    Explains what student data is collected, how it is processed, and user privacy rights.
                  </p>
                </div>
                <a
                  routerLink="/privacy"
                  target="_blank"
                  class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary gap-1.5 self-start sm:self-auto"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i> View Live Page
                </a>
              </div>

              <!-- Mode Selection -->
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.privacyMode === 'default'"
                  [class.bg-primary/5]="settingsForm.value.privacyMode === 'default'"
                  [class.border-base-300]="settingsForm.value.privacyMode !== 'default'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="privacyMode"
                      value="default"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">Platform Default</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Standard platform privacy policy with your academy name dynamically applied.
                      </span>
                    </div>
                  </div>
                </label>

                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.privacyMode === 'link'"
                  [class.bg-primary/5]="settingsForm.value.privacyMode === 'link'"
                  [class.border-base-300]="settingsForm.value.privacyMode !== 'link'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="privacyMode"
                      value="link"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">External Link</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Redirect visitors directly to your hosted privacy policy URL.
                      </span>
                    </div>
                  </div>
                </label>

                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.privacyMode === 'custom'"
                  [class.bg-primary/5]="settingsForm.value.privacyMode === 'custom'"
                  [class.border-base-300]="settingsForm.value.privacyMode !== 'custom'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="privacyMode"
                      value="custom"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">Custom Page</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Write and design custom privacy policy using the rich text editor.
                      </span>
                    </div>
                  </div>
                </label>
              </div>

              <!-- External Link Configuration -->
              @if (settingsForm.value.privacyMode === 'link') {
                <div class="daisy-card bg-base-200/40 border border-base-300 p-4 space-y-2">
                  <label class="daisy-label py-0">
                    <span class="daisy-label-text font-medium text-xs">External Privacy Policy URL</span>
                  </label>
                  <div class="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      formControlName="privacyUrl"
                      placeholder="https://myacademy.com/privacy-policy"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs flex-1"
                    />
                    @if (settingsForm.value.privacyUrl) {
                      <a
                        [href]="settingsForm.value.privacyUrl"
                        target="_blank"
                        class="daisy-btn daisy-btn-sm daisy-btn-outline gap-1.5"
                      >
                        <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i> Test Link
                      </a>
                    }
                  </div>
                  <p class="text-[11px] text-base-content/60">
                    Visitors navigating to <code>/privacy</code> on your academy site will be automatically redirected to this URL.
                  </p>
                </div>
              }

              <!-- Custom Quill Editor Configuration -->
              @if (settingsForm.value.privacyMode === 'custom') {
                <div class="space-y-3 pt-2">
                  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label class="daisy-label py-0">
                      <span class="daisy-label-text font-medium text-xs">Privacy Policy Content</span>
                    </label>
                    <button
                      type="button"
                      (click)="loadPrivacyTemplate()"
                      class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-secondary gap-1.5"
                    >
                      <i class="fa-solid fa-file-lines text-[10px]"></i> Load Boilerplate Template
                    </button>
                  </div>
                  <black-rich-text-editor
                    formControlName="privacyContent"
                    placeholder="Draft your Privacy Policy..."
                    theme="snow"
                    minHeight="300px"
                  ></black-rich-text-editor>
                </div>
              }
            </div>

            <!-- SECTION 3: REFUND POLICY -->
            <div class="mt-10 space-y-4 pt-6 border-t border-base-200">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-base-200 pb-2">
                <div>
                  <h3 class="font-bold text-sm text-base-content flex items-center gap-2">
                    <i class="fa-solid fa-rotate-left text-primary"></i> Refund Policy
                  </h3>
                  <p class="text-xs text-base-content/70">
                    Defines eligibility windows, money-back guarantees, and request processes for course purchases.
                  </p>
                </div>
                <a
                  routerLink="/refund"
                  target="_blank"
                  class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary gap-1.5 self-start sm:self-auto"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i> View Live Page
                </a>
              </div>

              <!-- Mode Selection -->
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.refundMode === 'default'"
                  [class.bg-primary/5]="settingsForm.value.refundMode === 'default'"
                  [class.border-base-300]="settingsForm.value.refundMode !== 'default'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="refundMode"
                      value="default"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">Platform Default</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Standard platform refund policy with your academy name dynamically applied.
                      </span>
                    </div>
                  </div>
                </label>

                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.refundMode === 'link'"
                  [class.bg-primary/5]="settingsForm.value.refundMode === 'link'"
                  [class.border-base-300]="settingsForm.value.refundMode !== 'link'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="refundMode"
                      value="link"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">External Link</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Redirect visitors directly to your hosted refund policy URL.
                      </span>
                    </div>
                  </div>
                </label>

                <label
                  class="daisy-card bg-base-200/50 hover:bg-base-200 border cursor-pointer p-4 transition-all"
                  [class.border-primary]="settingsForm.value.refundMode === 'custom'"
                  [class.bg-primary/5]="settingsForm.value.refundMode === 'custom'"
                  [class.border-base-300]="settingsForm.value.refundMode !== 'custom'"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="radio"
                      formControlName="refundMode"
                      value="custom"
                      class="daisy-radio daisy-radio-primary daisy-radio-sm mt-0.5"
                    />
                    <div>
                      <span class="font-semibold text-xs block text-base-content">Custom Page</span>
                      <span class="text-[11px] text-base-content/70 block mt-0.5">
                        Write and design custom refund policy using the rich text editor.
                      </span>
                    </div>
                  </div>
                </label>
              </div>

              <!-- External Link Configuration -->
              @if (settingsForm.value.refundMode === 'link') {
                <div class="daisy-card bg-base-200/40 border border-base-300 p-4 space-y-2">
                  <label class="daisy-label py-0">
                    <span class="daisy-label-text font-medium text-xs">External Refund Policy URL</span>
                  </label>
                  <div class="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      formControlName="refundUrl"
                      placeholder="https://myacademy.com/refund-policy"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs flex-1"
                    />
                    @if (settingsForm.value.refundUrl) {
                      <a
                        [href]="settingsForm.value.refundUrl"
                        target="_blank"
                        class="daisy-btn daisy-btn-sm daisy-btn-outline gap-1.5"
                      >
                        <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i> Test Link
                      </a>
                    }
                  </div>
                  <p class="text-[11px] text-base-content/60">
                    Visitors navigating to <code>/refund</code> on your academy site will be automatically redirected to this URL.
                  </p>
                </div>
              }

              <!-- Custom Quill Editor Configuration -->
              @if (settingsForm.value.refundMode === 'custom') {
                <div class="space-y-3 pt-2">
                  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label class="daisy-label py-0">
                      <span class="daisy-label-text font-medium text-xs">Refund Policy Content</span>
                    </label>
                    <button
                      type="button"
                      (click)="loadRefundTemplate()"
                      class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-secondary gap-1.5"
                    >
                      <i class="fa-solid fa-file-lines text-[10px]"></i> Load Boilerplate Template
                    </button>
                  </div>
                  <black-rich-text-editor
                    formControlName="refundContent"
                    placeholder="Draft your Refund Policy..."
                    theme="snow"
                    minHeight="300px"
                  ></black-rich-text-editor>
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- ========================================== -->
      <!-- TAB 2: LEGAL & INVOICING                   -->
      <!-- ========================================== -->
      @if (activeTab === 'legal') {
        <div class="daisy-card bg-base-100 shadow-md p-6 border border-base-200 space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-base-200 pb-4">
            <div>
              <h2 class="text-lg font-semibold text-base-content flex items-center gap-2">
                <i class="fa-solid fa-scale-balanced text-primary"></i> Legal & Invoicing Entity Details
              </h2>
              <p class="text-xs text-base-content opacity-70 mt-0.5">
                Configure your registered company legal name, tax / VAT registration number, registered business address, and invoice footer notes.
              </p>
            </div>
            <div>
              @if (!isLegalCustomEnabled) {
                <span class="daisy-badge daisy-badge-neutral daisy-badge-md gap-1.5 py-3 px-3 font-medium text-xs">
                  <i class="fa-solid fa-lock text-warning"></i> Platform Managed
                </span>
              } @else {
                <span class="daisy-badge daisy-badge-primary daisy-badge-md gap-1.5 py-3 px-3 font-medium text-xs">
                  <i class="fa-solid fa-scale-balanced"></i> Custom Details
                </span>
              }
            </div>
          </div>

          @if (!isLegalCustomEnabled) {
            <!-- Platform Managed Info Message -->
            <div class="daisy-card bg-base-200/60 border border-base-300 p-8 text-center space-y-4">
              <div class="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto text-2xl">
                <i class="fa-solid fa-file-invoice"></i>
              </div>
              <div class="max-w-md mx-auto space-y-2">
                <h3 class="font-bold text-base text-base-content">Platform Managed Legal & Invoicing</h3>
                <p class="text-xs text-base-content/70">
                  Student invoices and receipts currently use standard platform legal entity information. Contact platform administrators if your academy requires custom legal entity details.
                </p>
              </div>
            </div>
          } @else {
            <!-- Custom Legal Entity Fields -->
            <div class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="daisy-form-control">
                  <label class="daisy-label py-1">
                    <span class="daisy-label-text font-medium text-xs">Registered Company Name</span>
                  </label>
                  <input
                    type="text"
                    formControlName="legalCompanyName"
                    placeholder="e.g. Royal Education Academy (Pvt) Ltd"
                    class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                  />
                </div>

                <div class="daisy-form-control">
                  <label class="daisy-label py-1">
                    <span class="daisy-label-text font-medium text-xs">Tax / VAT Registration ID</span>
                  </label>
                  <input
                    type="text"
                    formControlName="legalTaxId"
                    placeholder="e.g. VAT-987654321"
                    class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                  />
                </div>
              </div>

              <div class="daisy-form-control">
                <label class="daisy-label py-1">
                  <span class="daisy-label-text font-medium text-xs">Registered Business Address</span>
                </label>
                <input
                  type="text"
                  formControlName="legalAddress"
                  placeholder="123 Education Ave, Suite 400"
                  class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="daisy-form-control">
                  <label class="daisy-label py-1">
                    <span class="daisy-label-text font-medium text-xs">City</span>
                  </label>
                  <input
                    type="text"
                    formControlName="legalCity"
                    placeholder="Colombo"
                    class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                  />
                </div>

                <div class="daisy-form-control">
                  <label class="daisy-label py-1">
                    <span class="daisy-label-text font-medium text-xs">Country</span>
                  </label>
                  <input
                    type="text"
                    formControlName="legalCountry"
                    placeholder="Sri Lanka"
                    class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="daisy-form-control">
                  <label class="daisy-label py-1">
                    <span class="daisy-label-text font-medium text-xs">Support / Billing Contact Email</span>
                  </label>
                  <input
                    type="email"
                    formControlName="legalContactEmail"
                    placeholder="billing@myacademy.lk"
                    class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                  />
                </div>

                <div class="daisy-form-control">
                  <label class="daisy-label py-1">
                    <span class="daisy-label-text font-medium text-xs">Support Contact Phone</span>
                  </label>
                  <input
                    type="text"
                    formControlName="legalContactPhone"
                    placeholder="+94 77 123 4567"
                    class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                  />
                </div>
              </div>

              <div class="daisy-form-control">
                <label class="daisy-label py-1">
                  <span class="daisy-label-text font-medium text-xs">Invoice Footer Note / Payment Terms</span>
                </label>
                <textarea
                  formControlName="legalFooterNote"
                  rows="2"
                  placeholder="e.g. Thank you for learning with us! For billing inquiries, contact billing@myacademy.lk."
                  class="daisy-textarea daisy-textarea-bordered text-xs"
                ></textarea>
              </div>
            </div>
          }
        </div>
      }

      <!-- ========================================== -->
      <!-- TAB 3: PAYMENTS & BANK TRANSFER            -->
      <!-- ========================================== -->
      @if (activeTab === 'payments') {
        <div class="daisy-card bg-base-100 shadow-md p-6 border border-base-200 space-y-6">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-base-200 pb-4">
            <div>
              <h2 class="text-lg font-semibold text-base-content flex items-center gap-2">
                <i class="fa-solid fa-building-columns text-primary"></i> Payment Methods & Direct Bank Transfer
              </h2>
              <p class="text-xs text-base-content opacity-70 mt-0.5">
                Configure accepted student payment methods, your academy's bank account for direct bank transfer deposits, and payment gateway credentials.
              </p>
            </div>
            <div>
              @if (!isPaymentsCustomEnabled) {
                <span class="daisy-badge daisy-badge-neutral daisy-badge-md gap-1.5 py-3 px-3 font-medium text-xs">
                  <i class="fa-solid fa-lock text-warning"></i> Platform Managed
                </span>
              } @else {
                <span class="daisy-badge daisy-badge-primary daisy-badge-md gap-1.5 py-3 px-3 font-medium text-xs">
                  <i class="fa-solid fa-credit-card"></i> Custom Gateways Enabled
                </span>
              }
            </div>
          </div>

          @if (!isPaymentsCustomEnabled) {
            <!-- Platform Managed Info Message -->
            <div class="daisy-card bg-base-200/60 border border-base-300 p-8 text-center space-y-4">
              <div class="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto text-2xl">
                <i class="fa-solid fa-building-columns"></i>
              </div>
              <div class="max-w-md mx-auto space-y-2">
                <h3 class="font-bold text-base text-base-content">Platform Managed Checkout & Payments</h3>
                <p class="text-xs text-base-content/70">
                  Student purchases and course enrollments are processed via the platform's default payment processing gateways. Contact platform administrators if your academy requires custom payment gateways or direct bank transfer accounts.
                </p>
              </div>
            </div>
          } @else {
            <div class="space-y-6">
              <!-- Accepted Payment Methods -->
              <div class="daisy-card bg-base-200/40 p-4 border border-base-300 space-y-2">
                <label class="font-semibold text-xs text-base-content block">
                  Accepted Student Checkout Methods
                </label>
                <div class="flex flex-wrap gap-4 pt-1">
                  <label class="daisy-label cursor-pointer justify-start gap-2 py-0">
                    <input
                      type="checkbox"
                      formControlName="acceptBankTransfer"
                      class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                    />
                    <span class="daisy-label-text text-xs font-medium">Direct Bank Transfer / Deposit</span>
                  </label>

                  <!-- <label class="daisy-label cursor-pointer justify-start gap-2 py-0">
                    <input
                      type="checkbox"
                      formControlName="acceptPayHere"
                      class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                    />
                    <span class="daisy-label-text text-xs font-medium">PayHere Online Gateway</span>
                  </label>

                  <label class="daisy-label cursor-pointer justify-start gap-2 py-0">
                    <input
                      type="checkbox"
                      formControlName="acceptStripe"
                      class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                    />
                    <span class="daisy-label-text text-xs font-medium">Stripe Online Gateway</span>
                  </label> -->
                </div>
              </div>

              <!-- Direct Bank Account Deposit Details Card -->
              <div class="daisy-card bg-base-200/50 p-5 border border-base-300 space-y-4">
                <div class="flex items-center justify-between border-b border-base-300 pb-2">
                  <div class="font-bold text-sm flex items-center gap-2 text-base-content">
                    <i class="fa-solid fa-building-columns text-success text-base"></i> Direct Bank Transfer Account Details
                  </div>
                  <span class="daisy-badge daisy-badge-sm daisy-badge-outline text-[11px]">
                    Shown to students on bank checkout
                  </span>
                </div>

                <p class="text-xs text-base-content/70">
                  These bank details are displayed to students when they choose Bank Transfer at checkout to deposit fees.
                </p>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div class="daisy-form-control">
                    <label class="daisy-label py-1">
                      <span class="daisy-label-text font-medium text-xs">Bank Name</span>
                    </label>
                    <input
                      type="text"
                      formControlName="bankName"
                      placeholder="e.g. Commercial Bank of Ceylon / Bank of Ceylon"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                    />
                  </div>

                  <div class="daisy-form-control">
                    <label class="daisy-label py-1">
                      <span class="daisy-label-text font-medium text-xs">Account Holder Name</span>
                    </label>
                    <input
                      type="text"
                      formControlName="accountName"
                      placeholder="e.g. Royal Academy (Pvt) Ltd"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div class="daisy-form-control">
                    <label class="daisy-label py-1">
                      <span class="daisy-label-text font-medium text-xs">Account Number</span>
                    </label>
                    <input
                      type="text"
                      formControlName="accountNumber"
                      placeholder="e.g. 100012345678"
                      class="daisy-input daisy-input-bordered daisy-input-sm font-mono text-xs"
                    />
                  </div>

                  <div class="daisy-form-control">
                    <label class="daisy-label py-1">
                      <span class="daisy-label-text font-medium text-xs">Branch Name</span>
                    </label>
                    <input
                      type="text"
                      formControlName="branchName"
                      placeholder="e.g. Colombo Main / Kandy"
                      class="daisy-input daisy-input-bordered daisy-input-sm text-xs"
                    />
                  </div>

                  <div class="daisy-form-control">
                    <label class="daisy-label py-1">
                      <span class="daisy-label-text font-medium text-xs">SWIFT / BIC Code (Optional)</span>
                    </label>
                    <input
                      type="text"
                      formControlName="swiftCode"
                      placeholder="e.g. CCEYLKXX"
                      class="daisy-input daisy-input-bordered daisy-input-sm font-mono text-xs uppercase"
                    />
                  </div>
                </div>
              </div>

              <!-- PayHere Configuration (Optional / Secondary) -->
              @if (settingsForm.value.acceptPayHere) {
                <div class="daisy-card bg-base-200/50 p-4 border border-base-300 space-y-3">
                  <div class="font-bold text-xs flex items-center justify-between text-base-content border-b border-base-300 pb-2">
                    <span class="flex items-center gap-2">
                      <i class="fa-solid fa-credit-card text-info"></i> PayHere Online Gateway Credentials
                    </span>
                    <label class="daisy-label cursor-pointer gap-2 py-0">
                      <span class="daisy-label-text text-xs">Sandbox Mode</span>
                      <input
                        type="checkbox"
                        formControlName="payhereSandbox"
                        class="daisy-toggle daisy-toggle-sm daisy-toggle-warning"
                      />
                    </label>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="daisy-form-control">
                      <label class="daisy-label py-1">
                        <span class="daisy-label-text font-medium text-xs">PayHere Merchant ID</span>
                      </label>
                      <input
                        type="text"
                        formControlName="payhereMerchantId"
                        placeholder="e.g. 1234567"
                        class="daisy-input daisy-input-bordered daisy-input-sm font-mono text-xs"
                      />
                    </div>

                    <div class="daisy-form-control">
                      <label class="daisy-label py-1">
                        <span class="daisy-label-text font-medium text-xs">PayHere Merchant Secret</span>
                      </label>
                      <div class="relative">
                        <input
                          [type]="showPayhereSecret ? 'text' : 'password'"
                          formControlName="payhereMerchantSecret"
                          placeholder="Enter merchant secret"
                          class="daisy-input daisy-input-bordered daisy-input-sm w-full pr-8 font-mono text-xs"
                        />
                        <button
                          type="button"
                          (click)="showPayhereSecret = !showPayhereSecret"
                          class="absolute right-2 top-2 text-xs text-base-content/60 hover:text-base-content"
                        >
                          <i class="fa-solid" [class.fa-eye]="!showPayhereSecret" [class.fa-eye-slash]="showPayhereSecret"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              }

              <!-- Stripe Configuration (Optional / Secondary) -->
              @if (settingsForm.value.acceptStripe) {
                <div class="daisy-card bg-base-200/50 p-4 border border-base-300 space-y-3">
                  <div class="font-bold text-xs flex items-center gap-2 text-base-content border-b border-base-300 pb-2">
                    <i class="fa-brands fa-stripe text-primary text-base"></i> Stripe Gateway Credentials
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="daisy-form-control">
                      <label class="daisy-label py-1">
                        <span class="daisy-label-text font-medium text-xs">Publishable Key</span>
                      </label>
                      <input
                        type="text"
                        formControlName="stripePublishableKey"
                        placeholder="pk_live_..."
                        class="daisy-input daisy-input-bordered daisy-input-sm font-mono text-xs"
                      />
                    </div>

                    <div class="daisy-form-control">
                      <label class="daisy-label py-1">
                        <span class="daisy-label-text font-medium text-xs">Secret Key</span>
                      </label>
                      <div class="relative">
                        <input
                          [type]="showStripeSecretKey ? 'text' : 'password'"
                          formControlName="stripeSecretKey"
                          placeholder="sk_live_..."
                          class="daisy-input daisy-input-bordered daisy-input-sm w-full pr-8 font-mono text-xs"
                        />
                        <button
                          type="button"
                          (click)="showStripeSecretKey = !showStripeSecretKey"
                          class="absolute right-2 top-2 text-xs text-base-content/60 hover:text-base-content"
                        >
                          <i class="fa-solid" [class.fa-eye]="!showStripeSecretKey" [class.fa-eye-slash]="showStripeSecretKey"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="daisy-form-control">
                    <label class="daisy-label py-1">
                      <span class="daisy-label-text font-medium text-xs">Webhook Signing Secret</span>
                    </label>
                    <div class="relative">
                      <input
                        [type]="showStripeWebhookSecret ? 'text' : 'password'"
                        formControlName="stripeWebhookSecret"
                        placeholder="whsec_..."
                        class="daisy-input daisy-input-bordered daisy-input-sm w-full pr-8 font-mono text-xs"
                      />
                      <button
                        type="button"
                        (click)="showStripeWebhookSecret = !showStripeWebhookSecret"
                        class="absolute right-2 top-2 text-xs text-base-content/60 hover:text-base-content"
                      >
                        <i class="fa-solid" [class.fa-eye]="!showStripeWebhookSecret" [class.fa-eye-slash]="showStripeWebhookSecret"></i>
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- TAB 5: Website & Landing Page -->
      @if (activeTab === 'landingPage') {
        <div class="space-y-6">

          <!-- Studio Banner Card -->
          <div class="daisy-card bg-gradient-to-r from-primary/10 via-primary/5 to-base-200 border border-primary/20 p-6 rounded-2xl shadow-sm">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div class="space-y-2 max-w-xl">
                <div class="inline-flex items-center gap-2 bg-primary/20 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  <i class="fa-solid fa-wand-magic-sparkles"></i>
                  <span>Interactive Site Studio</span>
                </div>
                <h2 class="text-xl font-bold text-base-content">Customize Your Academy Website</h2>
                <p class="text-sm text-base-content/70 leading-relaxed">
                  Open our dedicated full-screen editor to customize your hero headline, formatted rich-text introduction, course catalog, FAQs, and preview live across Desktop, Tablet, and Mobile.
                </p>
              </div>

              <button
                type="button"
                (click)="openSiteStudio()"
                class="daisy-btn daisy-btn-primary daisy-btn-md gap-2 shrink-0 shadow-md"
              >
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
                <span>Open Site Studio Editor</span>
              </button>
            </div>
          </div>

          <!-- Quick Portal Mode Configuration Card -->
          <div class="daisy-card bg-base-100 border border-base-300 p-6 rounded-2xl shadow-sm space-y-4">
            <h3 class="text-base font-bold text-base-content flex items-center gap-2">
              <i class="fa-solid fa-sliders text-primary"></i>
              <span>Public Portal & Access Mode</span>
            </h3>

            <div class="p-4 rounded-xl bg-base-200/60 border border-base-300 flex items-center justify-between gap-4">
              <div>
                <p class="text-sm font-semibold text-base-content">Enable Public Landing Page</p>
                <p class="text-xs text-base-content/60 mt-0.5">
                  When enabled, unauthenticated visitors at your domain see your public website. When disabled, users are redirected straight to the login screen.
                </p>
              </div>
              <input
                type="checkbox"
                class="daisy-toggle daisy-toggle-primary daisy-toggle-lg"
                [checked]="!settingsForm.get('appMode')?.value"
                (change)="settingsForm.get('appMode')?.setValue(!$any($event.target).checked)"
              />
            </div>
          </div>

        </div>
      }

      <!-- Submit Button (Always Visible across tabs) -->
      <div class="flex items-center justify-between pt-2 border-t border-base-200">
        <span class="text-xs text-base-content/60">
          Changes will apply to your academy workspace immediately.
        </span>

        <button
          type="submit"
          [disabled]="saving"
          class="daisy-btn daisy-btn-primary min-w-36"
        >
          @if (saving) {
            <span class="daisy-loading daisy-loading-spinner daisy-loading-sm"></span>
            Saving...
          } @else {
            <i class="fa-solid fa-floppy-disk"></i>
            Save Changes
          }
        </button>
      </div>
    </form>
  }
</div>


